import {
  ConversationParticipantEntity,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import {
  getFriendlyConversationId,
  getFullName,
  getSiteName,
} from '@provision/formatting-utils';
import { portalPaths } from '@provision/portal-routes';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { isNullish } from 'remeda';
import {
  AddClientStep,
  type SavedClient,
} from 'src/components/AddClientModal/add-client-step';
import { AddSiteStep } from 'src/components/AddSiteModal/add-site-step';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer, useModalSteps } from 'src/components/ModalContainer';
import {
  Address,
  getPreparedConversationParticipantOptionGroup,
  PreparedConversationParticipantOption,
  useCreateConversation,
  useCurrentUserProfile,
  usePartnersOptions,
} from 'src/graphql';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNavigateRoute } from 'src/utils/routing';
import {
  ConversationFormFields,
  type ConversationPartner,
  type ConversationRequester,
  NewConversationFormValues,
  useNewConversationForm,
} from './use-new-conversation-form';

const toRequester = (client: SavedClient, partnerId: number): ConversationRequester | null => {
  if (!client.clientId) {
    return null;
  }

  const option: PreparedConversationParticipantOption = {
    id: client.clientId,
    value: client.clientId,
    label: getFullName(client).trim(),
    group: getPreparedConversationParticipantOptionGroup(ConversationParticipantEntity.Client),
    participantEntity: ConversationParticipantEntity.Client,
    conversationParticipantId: client.clientId,
    phoneNumber: client.primaryPhoneNumber?.value?.toString() ?? '',
    email: client.primaryEmail?.value ?? '',
    entityPartnerId: client.partnerId ?? partnerId,
    avatarFileKey: null,
  };

  return {
    id: option.id,
    label: option.label,
    participantEntity: ConversationParticipantEntity.Client,
    data: option,
    email: option.email,
  };
};

interface CreateConversationFieldsProps {
  formMethods: ReturnType<typeof useForm<NewConversationFormValues>>;
  partner: ConversationPartner | null;
}

const CreateConversationFields = ({ formMethods, partner }: CreateConversationFieldsProps) => {
  const { pushStep } = useModalSteps();
  const { setValue } = formMethods;

  const onCreateClient = async (partnerId: number) => {
    const created = await pushStep<SavedClient>(<AddClientStep partnerId={partnerId} />);
    const requester = created ? toRequester(created, partnerId) : null;
    if (!requester) {
      return;
    }
    setValue(ConversationFormFields.Requester, requester, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(ConversationFormFields.Site, null);
  };

  const onCreateSite = async (requester: PreparedConversationParticipantOption) => {
    const site = await pushStep<Address>(
      <AddSiteStep
        partnerId={requester.entityPartnerId}
        client={{ id: requester.id, name: requester.label, email: requester.email }}
      />,
    );
    if (!site) {
      return;
    }
    setValue(
      ConversationFormFields.Site,
      { id: site.addressId, label: getSiteName(site) },
      { shouldDirty: true },
    );
  };

  const { formFields } = useNewConversationForm({
    formMethods,
    partner,
    onCreateClient: (partnerId) => void onCreateClient(partnerId),
    onCreateSite: (requester) => void onCreateSite(requester),
  });

  return (
    <GenericForm<NewConversationFormValues> formMethods={formMethods}>
      {formFields}
    </GenericForm>
  );
};

export const AddConversationModal = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const partnerOptions = usePartnersOptions();
  const { closeModal } = useModalMethods(Modals.AddConversationModal);
  const { isOpened, modalProps } = useModalState(Modals.AddConversationModal);
  const { navigate } = useNavigateRoute();
  const { user } = useCurrentUserProfile();
  const { createConversation } = useCreateConversation();

  const defaultPartner = useMemo<ConversationPartner | null>(() => {
    if (!isNullish(selectedPartnerId)) {
      return { id: selectedPartnerId };
    }
    return partnerOptions.length === 1 ? partnerOptions[0] : null;
  }, [partnerOptions, selectedPartnerId]);

  const formMethods = useForm<NewConversationFormValues>({
    values: {
      [ConversationFormFields.Partner]: defaultPartner,
      [ConversationFormFields.Group]: null,
      [ConversationFormFields.Requester]: modalProps?.requester ?? null,
      [ConversationFormFields.Site]: modalProps?.site ?? null,
      [ConversationFormFields.ConversationType]: null,
      [ConversationFormFields.Urgency]: null,
      [ConversationFormFields.Subject]: '',
    },
    resetOptions: { keepDirtyValues: true },
    mode: 'onTouched',
  });
  const { control, handleSubmit, reset } = formMethods;

  const partner = useWatch({ control, name: ConversationFormFields.Partner });

  const close = useCallback(() => {
    reset();
    closeModal();
  }, [closeModal, reset]);

  const onSubmit = handleSubmit(async (values) => {
    const requester = values[ConversationFormFields.Requester];
    const type = values[ConversationFormFields.ConversationType];
    const urgency = values[ConversationFormFields.Urgency];
    const group = values[ConversationFormFields.Group];
    const conversationPartner = values[ConversationFormFields.Partner];
    if (!user?.id || !conversationPartner || !requester || !type || !urgency || !group) {
      return;
    }

    close();

    const createdConversation = await createConversation({
      conversationId: crypto.randomUUID(),
      partner: conversationPartner,
      subject: values[ConversationFormFields.Subject],
      requesterId: requester.id,
      requesterEmail: requester.email,
      requesterEntity: requester.participantEntity,
      agentId: user.id,
      type: type.id,
      urgencyId: urgency.id,
      groupId: group.id,
      address: values[ConversationFormFields.Site]?.id,
      participationMode: ConversationParticipationMode.Assist,
    });

    const friendlyId = getFriendlyConversationId({
      ...createdConversation!,
      partner: { partnerId: conversationPartner.id },
    });

    if (friendlyId) {
      navigate(portalPaths.conversation.full(friendlyId));
    }
  });

  return (
    <ModalContainer
      open={isOpened}
      title="Create conversation"
      onClose={close}
      data-testid="create-conversation-modal"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'create-conversation-cancel' },
        {
          label: 'Create conversation',
          kind: 'primary',
          onClick: onSubmit,
          testId: 'create-conversation-submit',
        },
      ]}
    >
      <CreateConversationFields formMethods={formMethods} partner={partner} />
    </ModalContainer>
  );
};
